import { formatDelta, formatMoney } from './analyticsUtils';

/** Plain-language status for recent orders list */
export function formatOrderStatusLabel(status) {
  const s = String(status || '').toLowerCase();
  const map = {
    pending: 'Awaiting payment',
    paid: 'Paid',
    processing: 'Processing',
    partially_shipped: 'Partially shipped',
    shipped: 'Shipped',
    completed: 'Delivered',
    cancelled: 'Cancelled',
  };
  return map[s] || s.replace(/_/g, ' ');
}

/**
 * Auto-generated insights for the selected period (easy to scan).
 * @param {object|null} data analytics API payload
 * @returns {string[]}
 */
export function buildAnalyticsInsights(data) {
  if (!data) return [];
  const insights = [];
  const period = data.period || {};
  const cmp = data.comparison || {};
  const pa = data.period_activity || {};
  const ship = data.shipping_insights || {};
  const ff = data.fulfillment || {};
  const sh = data.shipments || {};

  const revDelta = formatDelta(cmp.revenue_pct);
  if (revDelta.tone !== 'muted' && period.revenue > 0) {
    insights.push(
      `Revenue for this period is ${formatMoney(period.revenue)} (${revDelta.text} vs ${cmp.previous_range?.label || 'prior period'}).`
    );
  } else if (period.revenue > 0) {
    insights.push(`Revenue for this period is ${formatMoney(period.revenue)}.`);
  }

  if (period.orders > 0 && period.aov > 0) {
    insights.push(
      `Average order value (AOV) is ${formatMoney(period.aov)} — ${period.items_per_order ?? 0} items per order on average.`
    );
  }

  if (pa.checkout_conversion_pct != null && pa.orders_placed > 0) {
    insights.push(
      `${pa.orders_paid ?? 0} of ${pa.orders_placed} orders placed in this period were paid (${pa.checkout_conversion_pct}% checkout conversion).`
    );
  }

  const op = data.online_payments || {};
  if ((op.pending_count ?? 0) > 0) {
    insights.push(
      `${op.pending_count} order${op.pending_count === 1 ? '' : 's'} started Razorpay checkout but are not paid yet${op.pending_value > 0 ? ` (${formatMoney(op.pending_value)} outstanding)` : ''}.`
    );
  }
  if ((op.period_paid_razorpay_orders ?? 0) > 0) {
    insights.push(
      `${op.period_paid_razorpay_orders} Razorpay payment${op.period_paid_razorpay_orders === 1 ? '' : 's'} in this period (${formatMoney(op.period_paid_razorpay_revenue ?? 0)}).`
    );
  }
  const payMix = data.payment_methods || [];
  const onlineMix = payMix.find((m) => m.method === 'online');
  const offlineMix = payMix.find((m) => m.method === 'offline');
  if (onlineMix && offlineMix && period.revenue > 0) {
    const onlinePct = Math.round((onlineMix.revenue / period.revenue) * 100);
    if (onlinePct >= 50) {
      insights.push(`Most paid revenue (${onlinePct}%) came from online (Razorpay) checkout in this period.`);
    }
  }

  const backlog = ship.fulfillment_backlog ?? 0;
  if (backlog > 0) {
    insights.push(
      `${backlog} open order${backlog === 1 ? '' : 's'} still need packing or shipping (paid, processing, or partial).`
    );
  }

  if ((ff.partially_shipped ?? 0) > 0) {
    insights.push(
      `${ff.partially_shipped} order${ff.partially_shipped === 1 ? '' : 's'} partially shipped — finish remaining items from Orders.`
    );
  }

  if (sh.enabled && (sh.parcels_in_period ?? 0) > 0) {
    insights.push(
      `${sh.parcels_in_period} parcel${sh.parcels_in_period === 1 ? '' : 's'} dispatched in this period (${sh.units_shipped_in_period ?? 0} units).`
    );
  }

  if (ship.avg_days_paid_to_ship != null) {
    insights.push(
      `Average time from payment to first ship (orders shipped in period): ${ship.avg_days_paid_to_ship} day${ship.avg_days_paid_to_ship === 1 ? '' : 's'}.`
    );
  }

  if ((data.discounts?.orders ?? 0) > 0) {
    insights.push(
      `Discounts: ${formatMoney(data.discounts.total_amount)} on ${data.discounts.orders} orders (${data.discounts.pct_of_revenue}% of period revenue).`
    );
  }

  if ((data.customers_period?.repeat_rate_pct ?? 0) >= 20) {
    insights.push(
      `${data.customers_period.repeat_rate_pct}% of paid orders in this period came from returning customers.`
    );
  }

  if ((data.health?.cancellation_rate_pct ?? 0) > 5) {
    insights.push(
      `Cancellation rate in period: ${data.health.cancellation_rate_pct}% (${data.health.cancelled ?? 0} cancelled of ${data.health.placed ?? 0} placed).`
    );
  }

  return insights.slice(0, 8);
}
